import dayjs from "dayjs";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { ListCard, ListRow } from "@/components/ui/list-card";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import { useTimeStatus } from "../../../timeTracking/queries";
import { GuidelinesSection } from "../../../timeTracking/rules/GuidelinesSection";
import { SectionHeader } from "../../components/SectionHeader";
import { useClientProfile } from "../../context/ClientProfileContext";
import { ClientViewCard } from "./ClientViewCard";
import { MergeClientDialog } from "./MergeClientDialog";
import { SettingsSection } from "./SettingsSection";

/** Client-level settings: what the client sees in the portal, billing guidelines (corporate-paid
 * clients, firm admins with time tracking on), and merge, archive, delete. Portal access itself
 * lives on Overview. */
export const SettingsTab: FC = () => {
  const { clientId, client, deleteClient, matters, reloadClient, url } =
    useClientProfile();
  const time = useTimeStatus();
  const clientName =
    [client?.first_name, client?.last_name].filter(Boolean).join(" ") ||
    "this client";
  const [isSaving, setIsSaving] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const mergeSource = url.state.focus.merge ?? null;
  const [mergeOpen, setMergeOpen] = useState(Boolean(mergeSource));

  useEffect(() => {
    if (mergeSource) setMergeOpen(true);
  }, [mergeSource]);

  const corporatePaid = useMemo(
    () =>
      matters.matters.some((m) =>
        Boolean((m.individual as { company_id?: string | null })?.company_id)
      ),
    [matters.matters]
  );

  const setArchived = async (archived: boolean) => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      await ForeignNationalsAPI.setArchived(clientId, archived);
      await reloadClient();
      toast.success(
        archived ? `${clientName} archived` : `${clientName} restored`,
        {
          description: archived
            ? "Hidden from the clients list and dashboards; reminders are paused."
            : "Back in the clients list; reminders are on again."
        }
      );
    } catch (e) {
      toast.error(archived ? "Couldn't archive" : "Couldn't restore", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setIsSaving(false);
    }
  };

  const archived = Boolean(client?.archived);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <SectionHeader title="Settings" />
      <div className="flex min-h-0 flex-1 flex-col overflow-auto">
        <div className="mx-auto flex w-full max-w-204 flex-col gap-8 px-12 pt-6 pb-24">
          <ClientViewCard clientId={clientId} />

          {time.enabled && time.is_admin ? (
            corporatePaid || showGuidelines ? (
              <ListCard className="p-4">
                <GuidelinesSection
                  owner={{ foreign_national_id: clientId }}
                  ownerName={clientName}
                  canEdit
                />
              </ListCard>
            ) : (
              <SettingsSection title="Billing Guidelines">
                <ListRow
                  title="Outside-Counsel Guidelines"
                  description="Only needed when a company pays for this client's cases. None of their cases are linked to a company."
                  control={
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setShowGuidelines(true)}
                    >
                      Show Anyway
                    </Button>
                  }
                />
              </SettingsSection>
            )
          ) : null}

          <SettingsSection title="Data Management">
            <ListRow
              title="Merge a Duplicate"
              description="Move another record's cases, requests, documents and history onto this client. You choose which value wins for each conflicting field."
              control={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setMergeOpen(true)}
                  disabled={!client}
                  data-testid="settings-merge"
                >
                  Merge…
                </Button>
              }
            />
            <ListRow
              title={archived ? "Restore Client" : "Archive Client"}
              description={
                archived
                  ? `Archived ${
                      client?.archived_at
                        ? dayjs(client.archived_at).format("MMM D, YYYY")
                        : ""
                    }. Restoring puts them back in lists and turns reminders on again.`
                  : "Hides this client from the clients list and dashboards and pauses their reminders. Nothing is deleted, and you can restore them any time."
              }
              control={
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isSaving || !client}
                  onClick={() =>
                    archived ? void setArchived(false) : setConfirmArchive(true)
                  }
                  data-testid="settings-archive"
                >
                  {archived ? "Restore" : "Archive"}
                </Button>
              }
            />
            <ListRow
              title="Delete Client"
              description="Removes this client and everything attached to them. Prefer Archive if you might need the record again."
              control={
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-danger hover:text-danger"
                  onClick={() => deleteClient.open()}
                >
                  Delete Client
                </Button>
              }
            />
          </SettingsSection>
        </div>
      </div>

      {client ? (
        <MergeClientDialog
          open={mergeOpen}
          onOpenChange={(open) => {
            setMergeOpen(open);
            if (!open && mergeSource) url.setTab("settings");
          }}
          client={client}
          initialSourceId={mergeSource}
          onMerged={() => {
            reloadClient().catch(() => undefined);
            void matters.refresh();
          }}
        />
      ) : null}

      <AlertDialog open={confirmArchive} onOpenChange={setConfirmArchive}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {clientName}?</AlertDialogTitle>
            <AlertDialogDescription>
              They&apos;ll be hidden from the clients list and dashboards, and
              their reminders pause. Their cases and documents stay as they are.
              You can restore them from the Archived filter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmArchive(false);
                void setArchived(true);
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
